import {Select, TextInput} from '@mantine/core';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {IconMessage} from '@tabler/icons-react';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo, useState} from 'react';
import type {DocumentReviewRoundListItemResponse} from 'src/api/ProactApiClient';
import {
  DocumentReviewRequestStateEnum,
  DocumentReviewTypeEnum,
} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {useGetDocumentReviewRoundsListQuery} from 'src/redux/queries/documentReview';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useDebounce from 'src/shared/hooks/useDebounce';
import {
  DocumentReviewRoundStatePill,
  formatReviewDate,
} from '../../documentReviewHelpers';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const stateFilterOptions = [
  {value: '', label: 'All'},
  {value: String(DocumentReviewRequestStateEnum.Pending), label: 'Pending'},
  {value: String(DocumentReviewRequestStateEnum.Approved), label: 'Approved'},
  {value: String(DocumentReviewRequestStateEnum.Rejected), label: 'Rejected'},
  {value: String(DocumentReviewRequestStateEnum.Cancelled), label: 'Cancelled'},
];

/**
 * Server-paged list of every document Approval review round, across all
 * documents/folders — used by the Approvals tab of the Document Management
 * list page.
 */
export const DocumentApprovalsTab: React.FC = () => {
  const navigate = useAppNavigate();
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 300);
  const [stateFilter, setStateFilter] = useState<string>(
    String(DocumentReviewRequestStateEnum.Pending)
  );

  const columns = useMemo<MRT_ColumnDef<DocumentReviewRoundListItemResponse>[]>(
    () => [
      {
        id: 'documentName',
        header: 'Document',
        enableSorting: false,
        Cell: ({row}) => row.original.documentName,
      },
      {
        accessorKey: 'documentVersion',
        header: 'Version',
        enableSorting: false,
        size: 80,
      },
      {
        id: 'state',
        header: 'Status',
        enableSorting: false,
        Cell: ({row}) => <Pill {...DocumentReviewRoundStatePill(row.original.state)} />,
      },
      {
        accessorKey: 'requestedByName',
        header: 'Requested by',
        enableSorting: false,
      },
      {
        accessorKey: 'createdAt',
        header: 'Requested date',
        enableSorting: false,
        Cell: ({row}) => formatReviewDate(row.original.createdAt),
      },
      {
        accessorKey: 'resolvedByName',
        header: 'Resolved by',
        enableSorting: false,
      },
      {
        id: 'resolvedAt',
        header: 'Resolved date',
        enableSorting: false,
        Cell: ({row}) => {
          const {resolvedAt, resolutionComment} = row.original;
          if (!resolvedAt) return formatReviewDate(resolvedAt);
          return resolutionComment ? (
            <Tooltip label={resolutionComment} multiline w={260}>
              <span>
                {formatReviewDate(resolvedAt)} <IconMessage size={14} />
              </span>
            </Tooltip>
          ) : (
            formatReviewDate(resolvedAt)
          );
        },
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'document-approvals-tab',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 10},
    defaultShowColumnFilters: false,
  });
  const {start, length} = tableState;

  const {data, isLoading, isFetching, isError, refetch} =
    useGetDocumentReviewRoundsListQuery(
      {
        start,
        length,
        reviewType: DocumentReviewTypeEnum.Approval,
        states: stateFilter ? [+stateFilter] : undefined,
        searchText: debouncedSearchText || undefined,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      enableColumnFilters: false,
      enableSorting: false,
      columns,
      data: rows,
      getRowId: row => String(row.id),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Filters>
            <TextInput
              value={searchText}
              onChange={event => setSearchText(event.currentTarget.value)}
              placeholder="Search documents..."
              w={220}
            />
            <Select
              data={stateFilterOptions}
              value={stateFilter}
              onChange={value => setStateFilter(value ?? '')}
              allowDeselect={false}
              w={160}
            />
          </TableToolbar.Filters>
        </TableToolbar>
      ),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() =>
              navigate(
                PathUtility.DocumentManagerViewFile(row.original.documentId, 'content')
              )
            }
          />
        </TableRowActions>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};
